import {
  convertEventSelectToTarget,
  CustomDateRangePicker,
  formatDataToLabelValue,
  Select,
} from '@repo/ui';
import {
  GetAllCoursesUserUseCaseInterface,
  GetAllCurricularUnitsUserUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
  GetAllOrganizationsUserUseCaseInterface,
  GetAllSemestersUserUseCaseInterface,
} from 'domain/usecases';
import { FormikValues } from 'formik';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import React, { useEffect } from 'react';
import {
  Button,
  Col,
  FormFeedback,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from 'reactstrap';
import './styles.scss';

interface IProps {
  isRight: boolean;
  toggle: () => void;
  validation: FormikValues;
  showUnityFilter: boolean;
  filtersDisabled?: boolean;
  showSemesterFilter: boolean;
  groupsUser: GetAllGroupsUserUseCaseInterface.Result;
  coursesUser: GetAllCoursesUserUseCaseInterface.Result;
  requestGetAllCoursesUser: (entityId?: string) => void;
  semestersUser: GetAllSemestersUserUseCaseInterface.Result;
  organizationsUser: GetAllOrganizationsUserUseCaseInterface.Result;
  curricularUnitsUser: GetAllCurricularUnitsUserUseCaseInterface.Result;
  handleChangeFilter: (e: {
    entity: string;
    course: string;
    semester: string;
    curricularUnit: string;
    group: string;
  }) => void;
  removeSessionAndClearFiltersSalved: () => void;
}

export const OffcanvasFilters = ({
  toggle,
  isRight,
  validation,
  groupsUser,
  coursesUser,
  semestersUser,
  showUnityFilter,
  organizationsUser,
  showSemesterFilter,
  curricularUnitsUser,
  requestGetAllCoursesUser,
  handleChangeFilter,
  removeSessionAndClearFiltersSalved,
}: IProps) => {
  const { getLegends } = useLegends();

  const {
    loadingCourse,
    loadingCurricularUnit,
    loadingGroup,
    loadingSemester,
  } = useAppSelector((state) => state.loadingSlice);

  useEffect(() => {
    validation.setFieldValue('period', [
      new Date(validation.values.period[0]),
      new Date(validation.values.period[1]),
    ]);
  }, []);

  return (
    <Offcanvas
      isOpen={isRight}
      direction="end"
      toggle={toggle}
      id="offcanvasRight"
      className="border-bottom"
    >
      <OffcanvasHeader
        toggle={toggle}
        id="offcanvasRightLabel"
        className="border-0 border border-bottom border-light"
      >
        Filtros
      </OffcanvasHeader>
      <OffcanvasBody style={{ overflowY: 'auto' }} className="p-0">
        <div className="p-4">
          <React.Fragment>
            <Col>
              <div className="mb-3">
                <Label htmlFor="entity" className="form-label">
                  Organização
                </Label>
                <Select
                  isClearable
                  id="entity"
                  name="entity"
                  placeholder="Todas as Organizações"
                  value={validation.values.entity}
                  onChange={(e) => {
                    requestGetAllCoursesUser(e?.value || '');
                    handleChangeFilter({
                      entity: e?.value,
                      course: null,
                      curricularUnit: null,
                      semester: null,
                      group: null,
                    });
                    validation.setValues({
                      ...validation.values,
                      course: null,
                      semester: null,
                      unit: null,
                      group: null,
                    });
                    validation.handleChange(
                      convertEventSelectToTarget('entity', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={formatDataToLabelValue(
                    organizationsUser as never,
                    'id',
                    'nome',
                  )}
                />
              </div>
            </Col>

            <Col>
              <div className="mb-3">
                <Label htmlFor="course" className="form-label">
                  {`${getLegends('CURSO', false)}`}
                </Label>
                <Select
                  isClearable
                  id="course"
                  name="course"
                  placeholder="Todos os Cursos"
                  isLoading={loadingCourse}
                  isDisabled={loadingCourse}
                  value={validation.values.course}
                  onChange={(e) => {
                    validation.setValues({
                      ...validation.values,
                      unit: null,
                      group: null,
                      semester: null,
                    });
                    handleChangeFilter({
                      entity: validation.values.entity?.value,
                      course: e?.value,
                      curricularUnit: null,
                      semester: null,
                      group: null,
                    });
                    validation.handleChange(
                      convertEventSelectToTarget('course', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={formatDataToLabelValue(
                    coursesUser as never,
                    'id',
                    'nome',
                  )}
                />
              </div>
            </Col>
          </React.Fragment>
          {showSemesterFilter && (
            <Col>
              <div className="mb-3">
                <Label htmlFor="semester" className="form-label">
                  {`${getLegends('SEMESTRE', false)}`}
                </Label>
                <Select
                  isClearable
                  id="semester"
                  isLoading={loadingSemester}
                  name="semester"
                  placeholder="Todos os Semestres"
                  value={validation.values.semester}
                  onChange={(e) => {
                    handleChangeFilter({
                      entity: validation.values.entity?.value,
                      course: validation.values.course?.value,
                      curricularUnit: validation.values.curricularUnit?.value,
                      semester: e?.value,
                      group: validation.values.group?.value,
                    });
                    validation.handleChange(
                      convertEventSelectToTarget('semester', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={formatDataToLabelValue(
                    semestersUser as never,
                    'id',
                    'nome',
                  )}
                  isDisabled={!validation.values.entity || loadingSemester}
                />
              </div>
            </Col>
          )}

          {showUnityFilter && (
            <Col>
              <div className="mb-3">
                <Label htmlFor="unit" className="form-label">
                  {`${getLegends('UNIDADE_CURRICULAR', false)}`}
                </Label>
                <Select
                  isClearable
                  isLoading={loadingCurricularUnit}
                  id="unit"
                  name="unit"
                  placeholder="Todas as Unidades Curriculares"
                  value={validation.values.unit}
                  onChange={(e) => {
                    handleChangeFilter({
                      entity: validation.values.entity?.value,
                      course: validation.values.course?.value,
                      semester: validation.values.semester?.value,
                      curricularUnit: e?.value,
                      group: validation.values.group?.value,
                    });
                    validation.handleChange(
                      convertEventSelectToTarget('unit', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={formatDataToLabelValue(
                    curricularUnitsUser as never,
                    'id',
                    'nome',
                  )}
                  isDisabled={
                    !validation.values.entity || loadingCurricularUnit
                  }
                />
              </div>
            </Col>
          )}

          <Col>
            <div className="mb-3">
              <Label htmlFor="group" className="form-label">
                {`${getLegends('GRUPO', false)}`}
              </Label>
              <Select
                isClearable
                isLoading={loadingGroup}
                id="group"
                name="group"
                placeholder="Todos os Grupos"
                value={validation.values.group}
                onChange={(e) => {
                  handleChangeFilter({
                    entity: validation.values.entity?.value,
                    course: validation.values.course?.value,
                    semester: validation.values.semester?.value,
                    curricularUnit: validation.values.curricularUnit?.value,
                    group: e?.value,
                  });
                  validation.handleChange(
                    convertEventSelectToTarget('group', e),
                  );
                }}
                onBlur={validation.handleBlur}
                options={formatDataToLabelValue(
                  groupsUser as never,
                  'id',
                  'nome',
                )}
                isDisabled={!validation.values.entity || loadingGroup}
              />
            </div>
          </Col>

          <Col>
            <Row>
              <Label htmlFor="initial-date" className="form-label">
                Período:
              </Label>
              <CustomDateRangePicker
                initialStartDate={new Date(validation.values.period[0])}
                initialEndDate={new Date(validation.values.period[1])}
                cleanable={false}
                placement="leftStart"
                value={validation.values.period}
                menuClassName="custom-datepicker-menu"
                onChange={(selectedDates) => {
                  validation.handleChange({
                    target: {
                      id: 'period',
                      name: 'period',
                      value: selectedDates,
                    },
                  });
                }}
              />
            </Row>

            {validation.touched.period && validation.errors.period ? (
              <FormFeedback type="invalid">
                {validation.errors.period}
              </FormFeedback>
            ) : null}
          </Col>
        </div>
      </OffcanvasBody>
      <Button
        type="button"
        color="primary"
        className="text-center m-3"
        onClick={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <i className="ri-checkbox-circle-line align-middle mx-2"></i>
        Aplicar
      </Button>
      <p
        onClick={() => {
          removeSessionAndClearFiltersSalved();
          toggle();
        }}
        className="text-center text-decoration-underline text-primary cursor-pointer"
      >
        Limpar Filtros
      </p>
    </Offcanvas>
  );
};
